/**
 * AuthContext
 *
 * Provides current user state and auth actions (login / logout).
 * On mount, fetches /api/auth/me to restore session from HttpOnly cookie.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export interface AuthUser {
  id: string
  email: string
  displayName: string
  avatarUrl: string | null
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface AuthState {
  status: AuthStatus
  user: AuthUser | null
}

interface AuthContextValue extends AuthState {
  /** Redirect to Google OAuth login. Pass current path to return after login. */
  loginWithGoogle: (redirectTo?: string) => void
  /** Log out the current device only */
  logout: () => Promise<void>
  /** Log out all devices (invalidates all sessions server-side) */
  logoutAll: () => Promise<void>
  /** Permanently delete the account and all associated data */
  deleteAccount: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })

  // Restore session on mount
  useEffect(() => {
    const controller = new AbortController()

    fetch('/api/auth/me', { credentials: 'include', signal: controller.signal })
      .then(async (res) => {
        if (res.ok) {
          const user = await res.json() as AuthUser
          setState({ status: 'authenticated', user })
        } else {
          setState({ status: 'unauthenticated', user: null })
        }
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return
        setState({ status: 'unauthenticated', user: null })
      })

    return () => { controller.abort() }
  }, [])

  const loginWithGoogle = (redirectTo?: string) => {
    const params = new URLSearchParams()
    if (redirectTo) params.set('redirect_to', redirectTo)
    window.location.href = `/api/auth/login/google${params.size ? `?${params}` : ''}`
  }

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    setState({ status: 'unauthenticated', user: null })
  }

  const logoutAll = async () => {
    await fetch('/api/auth/logout-all', { method: 'POST', credentials: 'include' })
    setState({ status: 'unauthenticated', user: null })
  }

  const deleteAccount = async () => {
    await fetch('/api/user/account', { method: 'DELETE', credentials: 'include' })
    setState({ status: 'unauthenticated', user: null })
  }

  return (
    <AuthContext.Provider value={{ ...state, loginWithGoogle, logout, logoutAll, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
